import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import { ShippedItem } from "../../types/traceability";
import { kgFmt, lbsFmt, pcsFmt } from "../../shared/formatting";

interface ShippedItemsTableProps {
  items: ShippedItem[];
}

type ShippedItemRow = ShippedItem & {
  shipment_row_key: string;
};

/** Add stable, unique keys even when an order ships the same lot more than once. */
export function addShippedItemRowKeys(items: ShippedItem[]): ShippedItemRow[] {
  const occurrences = new Map<string, number>();

  return items.map((item) => {
    const baseKey = `${item.order_id}-${item.lot_number}`;
    const occurrence = occurrences.get(baseKey) ?? 0;
    occurrences.set(baseKey, occurrence + 1);

    return {
      ...item,
      shipment_row_key:
        occurrence === 0 ? baseKey : `${baseKey}-${occurrence}`,
    };
  });
}

export default function ShippedItemsTable({ items }: ShippedItemsTableProps) {
  const rows = React.useMemo(() => addShippedItemRowKeys(items), [items]);

  return (
    <DataTable
      value={rows}
      dataKey="shipment_row_key"
      responsiveLayout="scroll"
      size="small"
      className="p-datatable-sm"
      emptyMessage="No shipped items found"
    >
      <Column
        header="Customer"
        body={(row: ShippedItem) => row.order?.customer_name ?? "—"}
      />
      <Column
        header="Invoice / PO"
        body={(row: ShippedItem) =>
          row.order?.invoice_number ?? row.order?.po_number ?? "—"
        }
      />
      <Column
        header="Total kg"
        body={(row: ShippedItem) => kgFmt(row.total_in_kg)}
        style={{ minWidth: 120 }}
      />
      <Column
        header="Total lbs"
        body={(row: ShippedItem) => lbsFmt(row.total_in_lbs)}
        style={{ minWidth: 120 }}
      />
      <Column
        header="Total pcs"
        body={(row: ShippedItem) => pcsFmt(row.total_in_pieces)}
        style={{ minWidth: 120 }}
      />
      <Column
        header="Order"
        body={(row: ShippedItem) => (
          <button
            type="button"
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            onClick={() => window.open(`/shipments/${row.order_id}`, "_blank")}
          >
            View Order
          </button>
        )}
        style={{ minWidth: 120 }}
      />
    </DataTable>
  );
}
